import type { IPaymentService } from "@/services/interfaces/IPaymentService";
import type {
  EscrowRecord,
  TransactionResult,
  WalletBalance,
  Transaction,
  SupportedCurrency,
} from "@/lib/types";

const MOCK_DELAY_MS = 1000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generateId(): string {
  return `mock-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateTxHash(): string {
  const chars = "0123456789abcdef";
  let hash = "0x";
  for (let i = 0; i < 64; i++) {
    hash += chars[Math.floor(Math.random() * chars.length)];
  }
  return hash;
}

const MOCK_ESCROW_RECORDS: EscrowRecord[] = [
  {
    id: "mock-escrow-001",
    questId: "mock-quest-001",
    totalAmount: 500,
    currency: "USDT",
    status: "funded",
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: "mock-escrow-002",
    questId: "mock-quest-003",
    totalAmount: 800,
    currency: "USDT",
    status: "fully_released",
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
  },
];

const MOCK_TRANSACTIONS: Transaction[] = [
  {
    id: "mock-tx-001",
    type: "escrow_fund",
    amount: 500,
    currency: "USDT",
    fromAddress: "0xMOCK_WALLET_ADDRESS_1234",
    toAddress: "0xMOCK_ESCROW_CONTRACT",
    txHash: "0xabc123def456789012345678901234567890abcdef1234567890abcdef123456",
    status: "confirmed",
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: "mock-tx-002",
    type: "escrow_fund",
    amount: 800,
    currency: "USDT",
    fromAddress: "0xMOCK_WALLET_ADDRESS_1234",
    toAddress: "0xMOCK_ESCROW_CONTRACT",
    txHash: "0xdef789abc012345678901234567890abcdef1234567890abcdef1234567890ab",
    status: "confirmed",
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
  },
  {
    id: "mock-tx-003",
    type: "payment_release",
    amount: 150,
    currency: "USDT",
    fromAddress: "0xMOCK_ESCROW_CONTRACT",
    toAddress: "0xMOCK_HUNTER_WALLET_001",
    txHash: "0x123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef0",
    status: "confirmed",
    createdAt: new Date(Date.now() - 25 * 86400000).toISOString(),
  },
  {
    id: "mock-tx-004",
    type: "payment_release",
    amount: 650,
    currency: "USDT",
    fromAddress: "0xMOCK_ESCROW_CONTRACT",
    toAddress: "0xMOCK_HUNTER_WALLET_002",
    txHash: "0x789abcdef0123456789abcdef0123456789abcdef0123456789abcdef012345",
    status: "confirmed",
    createdAt: new Date(Date.now() - 20 * 86400000).toISOString(),
  },
];

export class MockPaymentService implements IPaymentService {
  private escrows: Map<string, EscrowRecord> = new Map();
  private transactions: Transaction[] = [];

  constructor() {
    for (const escrow of MOCK_ESCROW_RECORDS) {
      this.escrows.set(escrow.id, { ...escrow });
    }
    this.transactions = MOCK_TRANSACTIONS.map((tx) => ({ ...tx }));
  }

  async createEscrow(
    questId: string,
    amount: number,
    currency: SupportedCurrency,
  ): Promise<EscrowRecord> {
    await delay(MOCK_DELAY_MS);

    const id = generateId();
    const now = new Date().toISOString();

    const escrow: EscrowRecord = {
      id,
      questId,
      totalAmount: amount,
      currency,
      status: "funded",
      createdAt: now,
    };

    this.escrows.set(id, escrow);

    // Record the funding transaction
    this.transactions.push({
      id: generateId(),
      type: "escrow_fund",
      amount,
      currency,
      fromAddress: "0xMOCK_WALLET_ADDRESS_1234",
      toAddress: "0xMOCK_ESCROW_CONTRACT",
      txHash: generateTxHash(),
      status: "confirmed",
      createdAt: now,
    });

    return { ...escrow };
  }

  async releasePayment(
    escrowId: string,
    recipientAddress: string,
    amount: number,
  ): Promise<TransactionResult> {
    await delay(1500);

    const escrow = this.escrows.get(escrowId);
    if (!escrow) {
      throw new Error(`Escrow not found: ${escrowId}`);
    }

    const txHash = generateTxHash();
    const now = new Date().toISOString();

    // Record the release transaction
    this.transactions.push({
      id: generateId(),
      type: "payment_release",
      amount,
      currency: escrow.currency,
      fromAddress: "0xMOCK_ESCROW_CONTRACT",
      toAddress: recipientAddress,
      txHash,
      status: "confirmed",
      createdAt: now,
    });

    // Update escrow status
    this.escrows.set(escrowId, {
      ...escrow,
      status: "partially_released",
    });

    return {
      txHash,
      status: "confirmed",
    };
  }

  async getEscrowStatus(escrowId: string): Promise<EscrowRecord> {
    await delay(500);

    const escrow = this.escrows.get(escrowId);
    if (!escrow) {
      throw new Error(`Escrow not found: ${escrowId}`);
    }
    return { ...escrow };
  }

  async getBalance(walletAddress: string): Promise<WalletBalance> {
    await delay(500);

    return {
      address: walletAddress,
      balances: [
        { currency: "USDC", amount: 1250.0 },
        { currency: "USDT", amount: 875.5 },
      ],
    };
  }

  async getTransactionHistory(
    _walletAddress: string,
  ): Promise<Transaction[]> {
    await delay(MOCK_DELAY_MS);

    return this.transactions.map((tx) => ({ ...tx }));
  }
}
